import type {
  Ticket,
  TicketClassification,
  TicketPriority,
  TicketProduct,
  TicketStatus,
} from '@yhelp/shared-types';

/** ITIL record types — UI catalog (M1 static; M2 persisted in categories table). */
export type YsecCategoryId = 'incidente' | 'requisicao' | 'problema' | 'mudanca' | 'consulta';

export interface YsecCategory {
  id: YsecCategoryId;
  label: string;
  description: string;
}

export interface YsecProduct {
  value: TicketProduct;
  label: string;
  queueId: YsecQueueId;
  description: string;
}

export interface YsecQueue {
  id: YsecQueueId;
  label: string;
  description: string;
  /** Minimum role hint for UI (full RBAC enforced server-side). */
  minRole: 'agent_n1' | 'agent_n2' | 'support_manager';
  filter: (ticket: Ticket) => boolean;
}

export type YsecQueueId = 'triagem_n1' | 'cloudflare' | 'iam' | 'yface' | 'escalation_n2';

export interface TicketFilters {
  status?: TicketStatus | '';
  priority?: TicketPriority | '';
  product?: TicketProduct | '';
  classification?: TicketClassification | '';
  search?: string;
}

export const YSEC_CATEGORIES: YsecCategory[] = [
  {
    id: 'incidente',
    label: 'Incidente',
    description: 'Interrupção ou degradação de serviço',
  },
  {
    id: 'requisicao',
    label: 'Requisição',
    description: 'Solicitação de serviço padrão',
  },
  {
    id: 'problema',
    label: 'Problema',
    description: 'Causa raiz de incidentes recorrentes',
  },
  {
    id: 'mudanca',
    label: 'Mudança',
    description: 'Alteração planificada em produção',
  },
  {
    id: 'consulta',
    label: 'Consulta',
    description: 'Dúvida ou orientação',
  },
];

export const YSEC_PRODUCTS: YsecProduct[] = [
  {
    value: 'cloudflare',
    label: 'Cloudflare',
    queueId: 'cloudflare',
    description: 'Workers, Pages, WAF, Zero Trust',
  },
  {
    value: 'okta',
    label: 'Okta',
    queueId: 'iam',
    description: 'IAM, SSO, Lifecycle Management',
  },
  {
    value: 'auth0',
    label: 'Auth0',
    queueId: 'iam',
    description: 'CIAM, Organizations, Actions',
  },
  {
    value: 'yface',
    label: 'yFace',
    queueId: 'yface',
    description: 'Plataforma de identidade ySec',
  },
];

export const YSEC_CLASSIFICATIONS: { value: TicketClassification; label: string }[] = [
  { value: 'troubleshoot', label: 'Troubleshoot' },
  { value: 'melhoria', label: 'Melhoria' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'implementacao', label: 'Implementação' },
  { value: 'outro', label: 'Outro' },
];

export const YSEC_STATUSES: { value: TicketStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'aberto', label: 'Abertos' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'em_espera', label: 'Em espera' },
  { value: 'resolvido', label: 'Resolvidos' },
  { value: 'fechado', label: 'Fechados' },
];

export const YSEC_PRIORITIES: { value: TicketPriority | ''; label: string }[] = [
  { value: '', label: 'Todas' },
  { value: 'critico', label: 'Crítico' },
  { value: 'alto', label: 'Alto' },
  { value: 'medio', label: 'Médio' },
  { value: 'baixo', label: 'Baixo' },
];

const OPEN_STATUSES: TicketStatus[] = ['aberto', 'em_andamento', 'em_espera', 'reaberto'];

export const YSEC_QUEUES: YsecQueue[] = [
  {
    id: 'triagem_n1',
    label: 'Triagem N1',
    description: 'Chamados abertos aguardando primeira resposta',
    minRole: 'agent_n1',
    filter: (t) => t.status === 'aberto' && !t.assignee_id,
  },
  {
    id: 'cloudflare',
    label: 'Cloudflare',
    description: 'Fila especializada — edge, segurança e infraestrutura Cloudflare',
    minRole: 'agent_n2',
    filter: (t) => t.product === 'cloudflare' && OPEN_STATUSES.includes(t.status),
  },
  {
    id: 'iam',
    label: 'IAM (Okta / Auth0)',
    description: 'Identidade, acesso e CIAM — produtos Okta e Auth0',
    minRole: 'agent_n2',
    filter: (t) =>
      (t.product === 'okta' || t.product === 'auth0') && OPEN_STATUSES.includes(t.status),
  },
  {
    id: 'yface',
    label: 'yFace',
    description: 'Plataforma de identidade ySec',
    minRole: 'agent_n2',
    filter: (t) => t.product === 'yface' && OPEN_STATUSES.includes(t.status),
  },
  {
    id: 'escalation_n2',
    label: 'Escalation N2',
    description: 'Chamados críticos ou de alta prioridade em atendimento',
    minRole: 'support_manager',
    filter: (t) =>
      (t.priority === 'critico' || t.priority === 'alto') && t.status === 'em_andamento',
  },
];

export const YSEC_INTEGRATIONS = [
  {
    id: 'auth0',
    label: 'Auth0',
    status: 'connected' as const,
    description: 'Login corporativo + SCIM',
  },
  {
    id: 'slack',
    label: 'Slack',
    status: 'connected' as const,
    description: 'Notificações de chamados',
  },
  {
    id: 'jira',
    label: 'Jira',
    status: 'planned' as const,
    description: 'Sync bidirecional de issues',
  },
  {
    id: 'pagerduty',
    label: 'PagerDuty',
    status: 'planned' as const,
    description: 'Bridge de incidentes críticos',
  },
  {
    id: 'teams',
    label: 'Microsoft Teams',
    status: 'planned' as const,
    description: 'Webhooks de alerta',
  },
] as const;

export function productLabel(product?: TicketProduct | null): string {
  if (!product) return '—';
  return YSEC_PRODUCTS.find((p) => p.value === product)?.label ?? product;
}

export function classificationLabel(classification?: TicketClassification | null): string {
  if (!classification) return '—';
  return YSEC_CLASSIFICATIONS.find((c) => c.value === classification)?.label ?? classification;
}

export function getQueueById(id: YsecQueueId): YsecQueue | undefined {
  return YSEC_QUEUES.find((q) => q.id === id);
}

export function filterTicketsByQueue(tickets: Ticket[], queueId: YsecQueueId): Ticket[] {
  const queue = getQueueById(queueId);
  if (!queue) return [];
  return tickets.filter(queue.filter);
}

export function countByQueue(tickets: Ticket[]): Record<YsecQueueId, number> {
  return YSEC_QUEUES.reduce(
    (acc, q) => {
      acc[q.id] = tickets.filter(q.filter).length;
      return acc;
    },
    {} as Record<YsecQueueId, number>,
  );
}

export function filterTicketsAdvanced(tickets: Ticket[], filters: TicketFilters): Ticket[] {
  const search = filters.search?.trim().toLowerCase() ?? '';
  return tickets.filter((t) => {
    if (filters.priority && t.priority !== filters.priority) return false;
    if (filters.product && t.product !== filters.product) return false;
    if (filters.classification && t.classification !== filters.classification) return false;
    if (search) {
      const hay = `${t.number} ${t.title}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

export function hasActiveFilters(filters: TicketFilters): boolean {
  return Boolean(
    filters.priority || filters.product || filters.classification || filters.search?.trim(),
  );
}

/** Re-export classification styles for ClassificationBadge compatibility */
export const CLASSIFICATION_STYLES: Record<
  TicketClassification,
  { bg: string; text: string; ring: string }
> = {
  troubleshoot: {
    bg: 'bg-red-100 dark:bg-red-950',
    text: 'text-red-800 dark:text-red-200',
    ring: 'ring-red-300 dark:ring-red-800',
  },
  melhoria: {
    bg: 'bg-blue-100 dark:bg-blue-950',
    text: 'text-blue-800 dark:text-blue-200',
    ring: 'ring-blue-300 dark:ring-blue-800',
  },
  feedback: {
    bg: 'bg-purple-100 dark:bg-purple-950',
    text: 'text-purple-800 dark:text-purple-200',
    ring: 'ring-purple-300 dark:ring-purple-800',
  },
  implementacao: {
    bg: 'bg-green-100 dark:bg-green-950',
    text: 'text-green-800 dark:text-green-200',
    ring: 'ring-green-300 dark:ring-green-800',
  },
  outro: {
    bg: 'bg-neutral-200 dark:bg-neutral-800',
    text: 'text-neutral-800 dark:text-neutral-200',
    ring: 'ring-neutral-300 dark:ring-neutral-700',
  },
};

/** @deprecated Use YSEC_PRODUCTS from ysec-catalog */
export const TICKET_PRODUCTS = YSEC_PRODUCTS.map(({ value, label }) => ({ value, label }));

/** @deprecated Use YSEC_CLASSIFICATIONS from ysec-catalog */
export const TICKET_CLASSIFICATIONS = YSEC_CLASSIFICATIONS;
